<template>
    <div class="newsletter-widget-container">
        <div class="section-header">
            <h2 class="section-heading">
                Newsletter
                <span v-if="unreadCount > 0" class="badge-count">{{ unreadCount }}</span>
            </h2>
            <span class="section-view-all" @click="handleViewAll">View All</span>
        </div>
        
        <div class="newsletter-list">
            <div v-if="loading" class="text-center p-3">
                 <div class="spinner-border text-primary" role="status"></div>
            </div>

            <p v-if="!loading && newsletters.length === 0" class="empty-state">No newsletters available</p>

            <template v-if="!loading && newsletters.length > 0">
                <div 
                    v-for="(item, index) in newsletters" 
                    :key="item.id || index"
                    class="newsletter-card"
                    :class="{ 'is-unread': !item.is_read }"
                    @click="handleClick(item)"
                 >
                    <div class="newsletter-info">
                        <div class="newsletter-image">
                            <img :src="item.cover_image || defaultLogo" alt="Newsletter">
                        </div>
                        <div class="newsletter-detail">
                            <h1 class="newsletter-title" :title="item.title">{{ truncate(item.title, 50) }}</h1>
                            <p class="newsletter-publisher">
                                <template v-if="item.merchant_id">
                                    {{ item.merchant_name }} • {{ item.branch_name }}
                                </template>
                                <template v-else-if="item.branch_id">
                                    {{ item.branch_name }}
                                </template>
                                <template v-else>
                                    {{ item.corporate_name }}
                                </template>
                            </p>
                            <div class="newsletter-date">{{ formatDate(item.published_at) }}</div>
                        </div>
                    </div>
                    <img src="/img/member-icon/arrow-right.svg" alt="" class="newsletter-chevron">

                    <div v-if="!item.is_read" class="newsletter-badge">
                        <span>New</span>
                    </div>
                 </div>
            </template>
        </div>
    </div>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

export default {
    name: 'MemberNewsletterWidget',
    props: {
        newsletters: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    setup() {
        const router = useRouter()
        const store = useStore()
        
        const unreadCount = computed(() => store.state.ClientNewsletter.unreadCount)
        
        const defaultLogo = computed(() => {
            const user = store.state.Auth.currUser || store.state.Auth.userInfo
            return user?.member?.corporate_logo || '/img/member-icon/tier-logo.svg'
        })

        onMounted(() => {
            store.dispatch('ClientNewsletter/getUnreadCount')
        })
        
        const handleViewAll = () => {
            router.push('/client/newsletters')
        }

        const handleClick = (item) => {
             store.dispatch('ClientNewsletter/markAsRead', item.id)
             router.push(`/client/newsletters/${item.id}`)
        }
        
        const formatDate = (date) => {
             if (!date) return ''
             return new Date(date).toLocaleDateString('en-US', {
                 year: 'numeric',
                 month: 'short',
                 day: '2-digit'
             })
        }

        const truncate = (text, length = 25) => {
            if (!text) return ''
            if (text.length <= length) return text
            return text.substring(0, length) + '..'
        }

        return {
            handleViewAll,
            handleClick,
            formatDate,
            unreadCount,
            truncate,
            defaultLogo
        }
    }
}
</script>

<style scoped>
.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.badge-count {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    background-color: var(--primary-color, #ef4444); /* Primary Brand */
    color: white;
    font-size: 12px;
    font-weight: 600;
    border-radius: 50%;
    min-width: 20px;
    height: 20px;
    padding: 0 4px;
    margin-left: 6px;
    vertical-align: middle;
    position: relative;
    top: -4px;
}

.section-heading {
    font-size: 18px;
    font-weight: 700;
    margin: 0;
    color: #111827;
}

.section-view-all {
    font-size: 14px;
    color: #717784;
    cursor: pointer;
}

.section-view-all:hover {
    color: var(--primary-color, #ef4444);
}

.newsletter-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    gap: 6px;
    align-items: stretch;
}

.newsletter-card {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background-color: white;
    border-radius: 12px;
    gap: 12px;
    padding: 16px;
    cursor: pointer;
    border: 1px solid #e1e4ea79; 
    transition: background-color 0.2s;
    overflow: hidden;
}

.newsletter-card:hover {
    background-color: #f9fafb;
}


.newsletter-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.newsletter-image {
    width: 44px;
    min-width: 44px;
    height: 44px;
    min-height: 44px;
    background-color: white;
    border-radius: 50%;
    border: 1px solid #E1E4EA;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.newsletter-image img {
    margin: auto;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.newsletter-detail {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.newsletter-title {
    font-size: 14px;
    font-weight: bold;
    color: #111827;
    line-height: 1.4;
    margin: 0;
    overflow-wrap: break-word;
    word-break: break-word;
}

.newsletter-publisher {
    font-size: 12px;
    font-weight: normal;
    color: #111827;
    line-height: 100%;
    margin: 0;
}

.newsletter-date {
    font-size: 12px;
    font-weight: normal;
    color: #99A0AE;
    line-height: 100%;
    white-space: nowrap;
}

.newsletter-badge {
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 38px 38px 0 0;
    border-color: var(--primary-color) transparent transparent transparent;
    z-index: 10;
}

.newsletter-badge span {
    position: absolute;
    top: -32px;
    left: 3px;
    font-size: 8px;
    font-weight: bold;
    color: white;
    text-transform: uppercase;
    white-space: nowrap;
    line-height: normal;
    transform: rotate(-45deg);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

.empty-state {
    text-align: center;
    color: #9ca3af;
    padding: 20px;
    font-size: 14px;
}

.newsletter-chevron {
    width: 20px;
    height: 20px;
    align-self: center;
    flex-shrink: 0;
    opacity: 0.6;
}

@media (max-width: 768px) {
    .newsletter-list {
        grid-template-columns: 1fr;
    }
}

.newsletter-widget-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
</style>
```
